import {
  FC,
  ForwardRefExoticComponent,
  PropsWithChildren,
  ReactElement,
  RefAttributes,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CategoryInterface, ProductInterface } from '../../../services/exports/Interfaces';
import CategoryItem from '../category/CategoryItem';
import useScreenType from '../../../hooks/utility/useScreenType';
import MenuBar from '../MenuNavigationBar/MenuBar';
import MenuList from './MenuList';
import MenuEmptyState from './MenuEmptyState';
import useMenu from '../../../hooks/menu/useMenu';
import { debounce } from 'lodash';
import classnames from 'classnames';
import useScrollPosition from '../../../hooks/utility/useScrollPosition';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import { useSearchParams } from 'react-router-dom';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { CompiledProduct, WithActualPrice } from '../../../providers/MenuProvider';
import { BasketLineItem } from '../../../store/types';
import useOrderMethodsStatus from '../../../hooks/availability/useOrderMethodsStatus';
import useCompanyHours from '../../../hooks/availability/useCompanyHours';
import usePageHeader from '../../../hooks/navigation/usePageHeader';
import { CategoryType } from '../../../services/exports/Constants';
import GiftCardCategory from '../../../features/gift-cards/components/category/GiftCardCategory';
import { MenuCategoryListProvider, useMenuCategoryList } from './MenuCategoryListProvider';

interface Props extends PropsWithChildren {
  className?: string;
  menuListClassName?: string;
  categoryListClassName?: string;
  skipProvider?: boolean;
  ListHeaderComponent?: ReactElement;
  ListFooterComponent?: ReactElement;
  DiscountsComponent?: ReactElement;
  LastOrderComponent?: ReactElement;
  onProductClick?: (product: ProductInterface | null) => void;
}

interface PropsWithClassName extends PropsWithChildren {
  className?: string;
}

const MenuCategoryList = forwardRef((props: Props, ref: any) => {
  const { isMobileApp } = useMobileApp();

  if (isMobileApp || props.skipProvider) {
    return <Content ref={ref} {...props} />;
  }

  return (
    <MenuCategoryListProvider>
      <Content ref={ref} {...props} />
    </MenuCategoryListProvider>
  );
}) as ForwardRefExoticComponent<Props & RefAttributes<any>> & {
  MenuBar: FC<PropsWithClassName>;
};

const Content = forwardRef((props: Props, ref: any) => {
  const {
    className,
    menuListClassName,
    categoryListClassName,
    ListHeaderComponent,
    ListFooterComponent,
    DiscountsComponent,
    LastOrderComponent,
    onProductClick,
    children,
  } = props;

  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { toggleMenuItemModal, toggleRestaurantClosedModal } = useAppMethods();
  const { selectedMenu } = useMenu();
  const { isMobileApp } = useMobileApp();
  const { scrollPosition } = useScrollPosition({ onScroll });
  const { isDesktop, isMobile } = useScreenType();
  const [searchParams] = useSearchParams();
  const { orderingDisabled } = useOrderMethodsStatus();
  const { ordersPausedUntil } = useCompanyHours();
  const searchString = searchParams.get('search');
  const { filteredCategories, setFocusedCategoryId, menuBarContainerRef } = useMenuCategoryList();
  const { pageHeaderHeight } = usePageHeader();
  const [activeScrollIndex, setActiveScrollIndex] = useState<number>(0);

  const categoriesRef = useRef(filteredCategories);
  categoriesRef.current = filteredCategories;

  const activeScrollIndexRef = useRef(activeScrollIndex);
  activeScrollIndexRef.current = activeScrollIndex;

  const scrollPositionRef = useRef(scrollPosition);
  scrollPositionRef.current = scrollPosition;

  const menuWrapperRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    toggleProductModal,
  }));

  function onScroll(): void {
    updateActiveCategory();
  }

  const updateActiveCategory = debounce(() => {
    const menuBarRect = menuBarContainerRef?.current?.getBoundingClientRect();
    const headerEndPosition =
      menuBarRect?.height > 0 ? menuBarRect.top + menuBarRect.height + 50 : pageHeaderHeight.desktop + 50;

    const categoryDivs = document.querySelectorAll('#category-list > div');
    let activeCategory: CategoryInterface;

    for (let i = 0; i < categoryDivs.length; i++) {
      const category = categoryDivs[i];

      const categoryStartPosition = category?.getBoundingClientRect()?.top;
      const categoryEndPosition = category?.getBoundingClientRect()?.height;

      if (categoryStartPosition < headerEndPosition && headerEndPosition < categoryEndPosition) {
        activeCategory = filteredCategories[i];
      }
    }

    setFocusedCategoryId(activeCategory?.id);
  }, 200);

  function toggleProductModal(product: CompiledProduct): void {
    if (orderingDisabled(cached_order?.method) && company?.has_live_orders) {
      return toggleRestaurantClosedModal({ openModal: true });
    }

    if (onProductClick) {
      return onProductClick(product);
    }

    const orderLineItem = (product as CompiledProduct & { order_line_item?: BasketLineItem }).order_line_item;

    return toggleMenuItemModal({
      openModal: true,
      product: product,
      ...(orderLineItem ? { lineItem: orderLineItem } : {}),
    });
  }

  function isActive(index: number): boolean {
    if (activeScrollIndexRef.current <= 3) {
      return index <= 6;
    }

    return index >= activeScrollIndexRef.current - 3 && index <= activeScrollIndexRef.current + 3;
  }

  const renderCategoryList = useMemo(() => {
    if (!filteredCategories.length) {
      return <MenuEmptyState searchString={searchString} isMobile={isMobile} />;
    }

    const getCategoryItemComponent = (
      category: CategoryInterface<WithActualPrice<ProductInterface>>,
      index: number,
    ) => {
      if (category.type !== CategoryType.GiftCards) {
        return (
          <CategoryItem
            category={category}
            index={index}
            isActive={isActive(index)}
            isDesktop={isDesktop}
            toggleProduct={toggleProductModal}
            onChangeVisibleIndex={setActiveScrollIndex}
          />
        );
      }

      return (
        <GiftCardCategory
          category={category}
          index={index}
          onChangeVisibleIndex={setActiveScrollIndex}
          isActive={isActive(index)}
        />
      );
    };

    const renderCategory = (
      category: CategoryInterface<WithActualPrice<ProductInterface>>,
      index: number,
    ): ReactElement => {
      const categoryItemComponent = getCategoryItemComponent(category, index);

      return (
        <div
          key={`category-${category.id}`}
          className={classnames('lg:px-none px-md mb-xl', {
            'px-none !mb-md': isMobileApp,
          })}
          id={`category-id-${category?.id}`}
        >
          {categoryItemComponent}
        </div>
      );
    };

    return (
      <div
        id="category-list"
        className={classnames(categoryListClassName, !isMobileApp && !isDesktop && 'mt-lg')}
        key={selectedMenu?.id?.toString()}
      >
        {filteredCategories?.map(renderCategory)}
      </div>
    );
  }, [
    selectedMenu,
    filteredCategories,
    cached_order?.method,
    activeScrollIndexRef.current,
    isDesktop,
    categoryListClassName,
    cached_order?.method,
    onProductClick,
    orderingDisabled,
    company,
    ordersPausedUntil,
  ]);

  return (
    <>
      <div id={'menu-category-list'} className={className}>
        <div ref={menuWrapperRef}>
          {ListHeaderComponent && ListHeaderComponent}
          {children}
          {!isMobileApp && !isDesktop && (
            <div
              className={classnames(menuListClassName, 'px-md lg:px-none py-sm bg-brand-background-primary')}
              id="menu-list"
            >
              <MenuList />
            </div>
          )}
          {!isMobileApp && <MenuCategoryList.MenuBar className="mb-lg" />}
          {DiscountsComponent}
          {!searchString?.length && LastOrderComponent}
          {renderCategoryList}
          {ListFooterComponent && ListFooterComponent}
        </div>
      </div>
    </>
  );
});

// eslint-disable-next-line react/prop-types
MenuCategoryList.MenuBar = ({ className, children }) => {
  const { menuBarContainerRef } = useMenuCategoryList();
  const { identityRowHeight } = usePageHeader();
  const { isMobileApp } = useMobileApp();

  return (
    <div
      className={classnames('w-full lg:hidden sticky z-50', className)}
      style={{ top: isMobileApp ? 0 : identityRowHeight }}
      ref={menuBarContainerRef}
    >
      <MenuBar />
      {children}
    </div>
  );
};

export default MenuCategoryList;
